<template>
  <article class="Stepper">
    <ul class="Stepper-list">
      <li
        v-for="(step, index) of steps"
        :key="index"
        role="listitem"
        class="Stepper-item"
        :class="{
          'is-active': index === activeStep,
          'is-previous': index < activeStep,
          'is-hidden': step.hidden
        }"
      >
        <div class="Stepper-itemVisualCue">
          <Icon v-if="step.icon" :icon="index < activeStep ? 'check' : step.icon" />
          <slot v-else name="step-number" :step="index + 1">
            {{ index + 1 }}.
          </slot>
        </div>

        <span class="Stepper-itemText">{{ step.text }}</span>
      </li>
    </ul>
  </article>
</template>

<script setup lang="ts">
import Icon from '@cvo/ui/components/atoms/Icon/Icon.vue';
import type { StepperItem } from './Stepper.model';

const { steps = [], activeStep = 0 } = defineProps<{
  activeStep?: number;
  steps?: StepperItem[];
}>();
</script>

<style lang="scss">
@use 'sass:math';
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  @mixin stepper-state(
    $bg-color,
    $border-color,
    $icon-color,
    $label-color,
    $text-color,
    $line-bg-color
  ) {
    .Stepper-itemVisualCue {
      color: $icon-color;
      background-color: $bg-color;
      border-color: $border-color;
    }

    &::before {
      background-color: $line-bg-color;
    }

    .Stepper-itemText {
      color: $label-color;
    }
  }

  .Stepper {
    $icon-size: 16px;
    $visualcue-size: $icon-size + vars.$molecules-stepper-stepperbase-padding-top-bottom * 2 + vars.$molecules-stepper-stepperbase-border-stroke * 2;

    max-width: 670px;
    margin-bottom: calc(vars.$dimensions-d-6);
    background-color: vars.$molecules-stepper-bg-color;
    border: vars.$molecules-stepper-border-stroke solid vars.$molecules-stepper-border-color;
    border-radius: vars.$molecules-stepper-lg-md-border-radius;
    padding: vars.$molecules-stepper-lg-md-padding-top-bottom vars.$molecules-stepper-lg-md-padding-left-right;

    @include responsive.mobile {
      border-radius: vars.$molecules-stepper-sm-border-radius;
      padding: vars.$molecules-stepper-sm-padding-top-bottom vars.$molecules-stepper-sm-padding-left-right;
    }

    &-list {
      display: flex;
      justify-content: space-around;
      flex-wrap: nowrap;
      list-style-type: none;
      padding: 0;
      margin: 0;
    }

    &-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: vars.$dimensions-d-1;

      position: relative;
      flex: 1;

      @include responsive.mobile {
        margin-bottom: 0;
      }

      // Icon/text "bubble"
      &VisualCue {
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;

        padding: vars.$molecules-stepper-stepperbase-padding-top-bottom vars.$molecules-stepper-stepperbase-padding-left-right;
        border-radius: vars.$molecules-stepper-stepperbase-border-radius;
        border-style: solid;
        border-width: vars.$molecules-stepper-stepperbase-border-stroke;

        --icon-size: #{$icon-size};

        &:not(:has(.Icon)) {
          @include layout.square($visualcue-size)
        }
      }

      // Horizontal connecting line
      &::before {
        content: '';

        position: absolute;
        top: math.div($visualcue-size, 2) - math.div(vars.$molecules-stepper-border-stroke, 2);

        display: block;
        height: vars.$molecules-stepper-border-stroke;
        width: 100%;
      }

      &:first-child::before {
        inset-inline-start: 50%;
        width: 50%;
      }

      &:last-child::before {
        inset-inline-end: 50%;
        width: 50%;
      }

      @include stepper-state(
        $bg-color: vars.$molecules-stepper-stepperbase-incomplete-bg-color,
        $border-color: vars.$molecules-stepper-stepperbase-incomplete-border-color,
        $icon-color: vars.$molecules-stepper-stepperbase-incomplete-icon-color,
        $label-color: vars.$molecules-stepper-stepperbase-incomplete-label-color,
        $text-color: vars.$molecules-stepper-stepperbase-incomplete-text-color,
        $line-bg-color: vars.$molecules-stepper-border-color
      );

      &.is-active {
        font-weight: vars.$typography-weight-strong;

        @include stepper-state(
          $bg-color: vars.$molecules-stepper-stepperbase-completing-bg-color,
          $border-color: vars.$molecules-stepper-stepperbase-completing-border-color,
          $icon-color: vars.$molecules-stepper-stepperbase-completing-icon-color,
          $label-color: vars.$molecules-stepper-stepperbase-completing-label-color,
          $text-color: vars.$molecules-stepper-stepperbase-completing-text-color,
          $line-bg-color: vars.$molecules-stepper-stepperbase-completing-border-color
        );
      }

      &.is-previous {
        @include stepper-state(
          $bg-color: vars.$molecules-stepper-stepperbase-complete-bg-color,
          $border-color: vars.$molecules-stepper-stepperbase-complete-border-color,
          $icon-color: vars.$molecules-stepper-stepperbase-complete-icon-check-color,
          $label-color: vars.$molecules-stepper-stepperbase-complete-label-color,
          $text-color: vars.$molecules-stepper-stepperbase-complete-text-color,
          $line-bg-color: vars.$molecules-stepper-stepperbase-completing-border-color
        );
      }

      &.is-hidden {
        display: none;
      }

      @include responsive.mobile {
        &::before {
          inset-inline-start: 50%;
        }

        &:first-child::before {
          width: 100%;
        }
        &:last-child::before {
          display: none;
        }
      }
    }

    &-itemText {
      @include text.text(sm);

      @include responsive.mobile {
        display: none;
      }
    }
  }
}
</style>
